import React from 'react';
import { Link } from 'react-router-dom';
import { useEdge, EdgeStatus } from './EdgeContext';

/**
 * EdgeStatusStrip
 *
 * Persistent thin health strip shown at the top of the app shell ONLY in edge
 * (local till) mode. Per design doc §17.10: independent plain-language chips,
 * each with at most one action hint. Non-blocking — never covers content.
 */

type Level = 'ok' | 'warn' | 'crit';

interface Chip {
  id: string;
  level: Level;
  label: string;
}

const SNAPSHOT_FRESH_SECONDS = 15 * 60;
const BILL_LOW_THRESHOLD = 20;

const LEVEL_STYLES: Record<Level, React.CSSProperties> = {
  ok: { backgroundColor: 'var(--status-ok-bg)', color: 'var(--status-ok-text)' },
  warn: { backgroundColor: 'var(--status-warn-bg)', color: 'var(--status-warn-text)' },
  crit: { backgroundColor: 'var(--status-bad-bg)', color: 'var(--status-bad-text)' },
};

const stripStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  minHeight: 30,
  padding: '2px 10px',
  fontSize: 13,
  lineHeight: '18px',
  backgroundColor: '#FAFAF9', /* basil-todo */
  borderBottom: '1px solid #E7E5E4', /* basil-todo */
  overflowX: 'auto',
  whiteSpace: 'nowrap',
  position: 'relative',
  zIndex: 200,
};

const chipStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: '2px 10px',
  borderRadius: 999,
  fontWeight: 600,
  fontVariantNumeric: 'tabular-nums',
  whiteSpace: 'nowrap',
};

const cashLinkStyle: React.CSSProperties = {
  ...chipStyle,
  marginLeft: 'auto',
  backgroundColor: '#E0E7FF', /* basil-todo */
  color: '#3730A3', /* basil-todo */
  textDecoration: 'none',
};

/** Compact edge-only nav entry to the cash drawer / refunds screen. */
const CashLink: React.FC = () => (
  <Link to="/edge/cash" data-testid="edge-cash-link" style={cashLinkStyle}>
    Cash / Refunds
  </Link>
);

export function buildChips(status: EdgeStatus): Chip[] {
  const chips: Chip[] = [];

  // Cloud / sync
  if (status.sync.mustReconcile > 0) {
    chips.push({
      id: 'sync',
      level: 'crit',
      label: `${status.sync.mustReconcile} sales need review — tell a manager`,
    });
  } else if (!status.cloud.reachable) {
    chips.push({
      id: 'sync',
      level: 'warn',
      label: 'Working offline — sales safe on this till',
    });
  } else {
    chips.push({
      id: 'sync',
      level: 'ok',
      label: `Synced · ${status.sync.pending} pending`,
    });
  }

  // Snapshot freshness
  const age = status.snapshot.ageSeconds;
  if (age !== null && age < SNAPSHOT_FRESH_SECONDS) {
    chips.push({ id: 'snapshot', level: 'ok', label: 'Menu up to date' });
  } else {
    const suffix = age !== null ? ` · synced ${Math.round(age / 60)}m ago` : '';
    chips.push({
      id: 'snapshot',
      level: 'warn',
      label: `Menu may be out of date${suffix}`,
    });
  }

  // Bill numbering
  if (status.numbering.mode === 'EXHAUSTED') {
    chips.push({
      id: 'numbering',
      level: 'crit',
      label: 'Cannot take payment — no bill numbers. Connect to internet.',
    });
  } else if (status.numbering.billRemaining <= BILL_LOW_THRESHOLD) {
    chips.push({
      id: 'numbering',
      level: 'warn',
      label: 'Bill numbers low — connect to internet soon',
    });
  } else {
    chips.push({
      id: 'numbering',
      level: 'ok',
      label: `Bills: ${status.numbering.billRemaining} left`,
    });
  }

  // Printer — quiet unless broken
  if (status.printer.status === 'ERROR') {
    chips.push({
      id: 'printer',
      level: 'crit',
      label: 'Printer problem — check paper',
    });
  }

  return chips;
}

const EdgeStatusStrip: React.FC = () => {
  const { isEdgeMode, status, agentUnreachable } = useEdge();

  if (!isEdgeMode) return null;

  // Agent unreachable — single critical strip
  if (agentUnreachable || !status) {
    // Before the first poll resolves show nothing rather than a false alarm.
    if (!agentUnreachable) return null;
    return (
      <div data-testid="edge-status-strip" style={stripStyle} role="status">
        <span data-testid="edge-chip-agent" style={{ ...chipStyle, ...LEVEL_STYLES.crit }}>
          Till service not responding — restarting may help
        </span>
        <CashLink />
      </div>
    );
  }

  const chips = buildChips(status);

  return (
    <div data-testid="edge-status-strip" style={stripStyle} role="status">
      {chips.map(chip => (
        <span
          key={chip.id}
          data-testid={`edge-chip-${chip.id}`}
          data-level={chip.level}
          style={{ ...chipStyle, ...LEVEL_STYLES[chip.level] }}
        >
          {chip.label}
        </span>
      ))}
      <CashLink />
    </div>
  );
};

export default EdgeStatusStrip;
